import React, { useState, useEffect } from "react";
import CustomSelect from "../ui/CustomSelect";
import Toast from "../ui/Toast";

const INPUT_CLASS =
  "w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)] disabled:bg-gray-50 disabled:cursor-not-allowed";

const LABEL_CLASS = "block text-sm font-medium text-gray-700 mb-1.5";

const FormSection = ({ title, description, children, isFirst }) => (
  <div
    className={`grid grid-cols-1 md:grid-cols-[minmax(180px,28%)_1fr] gap-4 md:gap-10 py-7 ${
      isFirst ? "" : "border-t border-gray-200"
    }`}
  >
    <div className="md:pt-0.5">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500 mt-1 leading-relaxed">{description}</p>
    </div>
    <div className="space-y-4 min-w-0">{children}</div>
  </div>
);

const PasswordToggleIcon = ({ visible }) =>
  visible ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

const AddUserToTenantModal = ({
  isOpen,
  onClose,
  onSubmit,
  tenants,
  initialTenantId = "",
}) => {
  const [tenantId, setTenantId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [role, setRole] = useState("viewer");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && initialTenantId) {
      setTenantId(String(initialTenantId));
    }
  }, [isOpen, initialTenantId]);

  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const resetForm = () => {
    setTenantId("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setRole("viewer");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!tenantId) {
      setError("Please select an organization (tenant)");
      return;
    }

    if (!firstName.trim()) {
      setError("First name is required");
      return;
    }

    if (!lastName.trim()) {
      setError("Last name is required");
      return;
    }

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!password) {
      setError("Password is required");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Password and Confirm Password do not match");
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        tenantId: parseInt(tenantId, 10),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        role,
      });
      resetForm();
      onClose();
    } catch (err) {
      const errorMessage = err.message || "Failed to add user";
      if (
        errorMessage.includes("409") ||
        errorMessage.toLowerCase().includes("already exists")
      ) {
        setError("This email already exists. Please use a different one.");
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const activeTenants = (tenants || []).filter((t) => t.isActive !== false);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-[999] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Add User to Existing Organization
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-gray-300"
            disabled={loading}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error ? (
          <Toast
            message={error}
            type="error"
            duration={5000}
            onClose={() => setError("")}
          />
        ) : null}

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 px-8">
            <FormSection
              isFirst
              title="Organization"
              description="Select the tenant this user belongs to."
            >
              <div>
                <label className={LABEL_CLASS}>
                  Organization <span className="text-[#D94436]">*</span>
                </label>
                <CustomSelect
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  className={INPUT_CLASS}
                  disabled={loading}
                  required
                >
                  <option value="">Select organization...</option>
                  {activeTenants.map((t) => (
                    <option key={t.tenantId} value={t.tenantId}>
                      {t.tenantName}
                    </option>
                  ))}
                </CustomSelect>
                {activeTenants.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1.5">
                    No active organizations. Create a tenant first.
                  </p>
                )}
              </div>
            </FormSection>

            <FormSection
              title="User Details"
              description="Profile, role, and login credentials for the new user."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>
                    First Name <span className="text-[#D94436]">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="First name"
                    disabled={loading}
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>
                    Last Name <span className="text-[#D94436]">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Last name"
                    disabled={loading}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div>
                <label className={LABEL_CLASS}>
                  Email Address <span className="text-[#D94436]">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="user@example.com"
                  disabled={loading}
                />
              </div>

              <div>
                <label className={LABEL_CLASS}>Role</label>
                <CustomSelect
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className={INPUT_CLASS}
                  disabled={loading}
                >
                  <option value="viewer">Read Only (view all data, no changes)</option>
                  <option value="admin">Admin (full access)</option>
                </CustomSelect>
                <p className="text-xs text-gray-500 mt-1.5">
                  Choose Admin for full access to manage data, or Read Only to view
                  without editing or deleting.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>
                    Password <span className="text-[#D94436]">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${INPUT_CLASS} pr-10`}
                      placeholder="Minimum 8 characters"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      disabled={loading}
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      <PasswordToggleIcon visible={showPassword} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>
                    Confirm Password <span className="text-[#D94436]">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`${INPUT_CLASS} pr-10`}
                      placeholder="Re-enter password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      disabled={loading}
                      tabIndex={-1}
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                    >
                      <PasswordToggleIcon visible={showConfirmPassword} />
                    </button>
                  </div>
                </div>
              </div>
            </FormSection>
          </div>

          <div className="flex justify-end gap-3 px-8 py-5 border-t border-gray-200 flex-shrink-0 bg-white">
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 text-sm font-medium bg-[var(--accent,#a51c1c)] text-white rounded-md hover:bg-[#8c1a1a] disabled:bg-[var(--accent,#a51c1c)]/50 disabled:cursor-not-allowed transition-colors"
              disabled={loading || activeTenants.length === 0}
            >
              {loading ? "Adding..." : "Add User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserToTenantModal;
